import type { KpiDto, KpiPoint } from '@bk/contracts';
import { useEffect, useRef, useState } from 'react';

/**
 * I grafici degli indicatori (ADR 0213 § 4): linea, colonne, barre o un numero solo, come dice la libreria. Una serie
 * prende il colore dell'azienda; i gruppi (sedi, canali) la tavolozza categoriale in ordine fisso, con la legenda; l'anno
 * prima è grigio. Ogni grafico ha il passaggio del mouse e la tabella dei valori.
 */

/** Tavolozza categoriale di riferimento (skill dataviz, superficie chiara), nell'ordine fisso. */
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
const PREVIOUS = '#a3a6ad';
const MONTHS = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

type Unit = NonNullable<KpiDto['libreria']>['unita'];

/** L'unità: dalla scheda della libreria, o scelta da chi ha creato il grafico. */
export const unitOf = (k: KpiDto): Unit => k.libreria?.unita ?? k.config.unita ?? 'numero';

/** Il valore nell'unità dell'indicatore: euro, percentuale (il rapporto × 100), numero. */
export function formatValue(v: number | null | undefined, unit: Unit, short = false): string {
  if (v == null) return '—';
  if (unit === 'percentuale') return `${(v * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`;
  if (unit === 'euro') return `${v.toLocaleString('it-IT', { maximumFractionDigits: short && Math.abs(v) >= 1000 ? 0 : 2, notation: short && Math.abs(v) >= 100_000 ? 'compact' : 'standard' })} €`;
  return v.toLocaleString('it-IT', { maximumFractionDigits: 1, notation: short && Math.abs(v) >= 100_000 ? 'compact' : 'standard' });
}

/** Il periodo detto per esteso: «ott 2026», «29 ott 2026», «2026». */
export function periodLabel(p: string): string {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(p);
  if (!m) return p === 'totale' ? 'Totale' : p;
  return `${m[3] ? `${Number(m[3])} ` : ''}${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** La larghezza del contenitore, per disegnare l'SVG senza deformare il testo. */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const o = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return { ref, width };
}

interface Series {
  key: string;
  label: string;
  color: string;
  values: Map<string, number | null>;
}

/** Le serie del grafico: una per gruppo (o una sola), più l'anno prima se c'è e non ci sono gruppi. */
function seriesOf(points: KpiPoint[]): { periods: string[]; series: Series[] } {
  const periods = [...new Set(points.map((p) => p.periodo))].sort();
  const grouped = points.some((p) => p.gruppo !== undefined);
  if (!grouped) {
    const own: Series = { key: 'valore', label: 'Quest’anno', color: 'var(--color-accent)', values: new Map(points.map((p) => [p.periodo, p.valore])) };
    const prev = points.some((p) => p.anno_prima != null) ? [{ key: 'anno_prima', label: 'L’anno prima', color: PREVIOUS, values: new Map(points.map((p) => [p.periodo, p.anno_prima ?? null])) }] : [];
    return { periods, series: [own, ...prev] };
  }
  // Un gruppo è sempre lo stesso colore: l'ordine è quello del primo valore, i gruppi oltre l'ottavo non hanno un colore.
  const groups = [...new Map(points.map((p) => [p.gruppo ?? '', p.etichetta ?? p.gruppo ?? ''])).entries()];
  return {
    periods,
    series: groups.slice(0, SERIES.length).map(([g, label], i) => ({
      key: g,
      label,
      color: SERIES[i]!,
      values: new Map(points.filter((p) => (p.gruppo ?? '') === g).map((p) => [p.periodo, p.valore])),
    })),
  };
}

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((x) => x * p).find((x) => x >= v)!;
};

/** Linea o colonne nel tempo, una scala sola. */
function TimeChart({ points, unit, kind }: { points: KpiPoint[]; unit: Unit; kind: 'linea' | 'colonne' }) {
  const { ref, width } = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const { periods: all, series } = seriesOf(points);
  // Gli ultimi 12 periodi: un anno a colpo d'occhio, il resto nella tabella.
  const periods = all.slice(-12);
  const H = 180;
  const pad = { l: 52, r: 8, t: 10, b: 24 };
  const w = Math.max(0, width - pad.l - pad.r);
  const h = H - pad.t - pad.b;
  const values = series.flatMap((s) => periods.map((p) => s.values.get(p) ?? null)).filter((v): v is number => v != null);
  const max = niceMax(Math.max(0, ...values));
  const y = (v: number) => pad.t + h - (v / max) * h;
  const band = periods.length ? w / periods.length : 0;
  const x = (i: number) => pad.l + band * i + band / 2;
  const ticks = [0, max / 2, max];
  const every = Math.ceil(periods.length / Math.max(1, Math.floor(w / 56)));
  const bars = kind === 'colonne' ? series : [];
  const barW = Math.min(24, Math.max(4, (band - 8) / Math.max(1, bars.length) - 2));
  return (
    <div ref={ref} className="relative">
      {width > 0 && (
        <svg width={width} height={H} role="img" aria-label="Grafico nel tempo" onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.l} x2={pad.l + w} y1={y(t)} y2={y(t)} stroke="#e7e8eb" strokeWidth={1} />
              <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#6b7080">
                {formatValue(t, unit, true)}
              </text>
            </g>
          ))}
          {periods.map((p, i) =>
            i % every === 0 ? (
              <text key={p} x={x(i)} y={H - 6} textAnchor="middle" fontSize={11} fill="#6b7080">
                {/* Per mese l'anno solo sul primo e su gennaio. */}
                {p.length === 7 && i !== 0 && !p.endsWith('-01') ? periodLabel(p).slice(0, 3) : periodLabel(p)}
              </text>
            ) : null,
          )}
          {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + h} stroke="#c9ccd3" strokeWidth={1} />}
          {kind === 'colonne'
            ? bars.map((s, si) =>
                periods.map((p, i) => {
                  const v = s.values.get(p);
                  if (v == null || v <= 0) return null;
                  const left = x(i) - (bars.length * (barW + 2)) / 2 + si * (barW + 2);
                  const top = y(v);
                  const height = pad.t + h - top;
                  const r = Math.min(4, height, barW / 2);
                  return <path key={`${s.key}-${p}`} d={`M${left},${pad.t + h} V${top + r} Q${left},${top} ${left + r},${top} H${left + barW - r} Q${left + barW},${top} ${left + barW},${top + r} V${pad.t + h} Z`} fill={s.color} opacity={hover === null || hover === i ? 1 : 0.55} />;
                }),
              )
            : series.map((s) => {
                const pts = periods.map((p, i) => [i, s.values.get(p)] as const).filter((q): q is readonly [number, number] => q[1] != null);
                return (
                  <g key={s.key}>
                    <polyline points={pts.map(([i, v]) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    {pts.map(([i, v]) => (
                      <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 5 : pts.length === 1 ? 4 : 0} fill={s.color} stroke="#fff" strokeWidth={2} />
                    ))}
                  </g>
                );
              })}
          {/* Le zone del passaggio del mouse: tutta la colonna del periodo, più grande del segno. */}
          {periods.map((p, i) => (
            <rect key={p} x={pad.l + band * i} y={pad.t} width={band} height={h} fill="transparent" onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
      )}
      {hover !== null && periods[hover] && (
        <div className="pointer-events-none absolute top-0 z-10 rounded-lg bg-ink px-2.5 py-1.5 text-xs text-white shadow-lg" style={{ left: Math.min(Math.max(0, x(hover) - 70), Math.max(0, width - 150)) }}>
          <p className="font-medium">{periodLabel(periods[hover]!)}</p>
          {series.map((s) => (
            <p key={s.key} className="flex items-center gap-1.5 tabular-nums">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {series.length > 1 && <span className="text-white/70">{s.label}</span>}
              <span>{formatValue(s.values.get(periods[hover]!), unit)}</span>
            </p>
          ))}
        </div>
      )}
      {series.length > 1 && <Legend series={series} />}
    </div>
  );
}

function Legend({ series }: { series: Series[] }) {
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink/70">
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />
          {s.label}
        </li>
      ))}
    </ul>
  );
}

/** Barre orizzontali per gruppo: l'ultimo periodo, o il totale. */
function GroupBars({ points, unit }: { points: KpiPoint[]; unit: Unit }) {
  const last = [...new Set(points.map((p) => p.periodo))].sort().at(-1);
  const rows = points.filter((p) => p.periodo === last && p.valore != null).sort((a, b) => b.valore! - a.valore!);
  const max = Math.max(0, ...rows.map((r) => r.valore!));
  return (
    <div>
      {last && last !== 'totale' && <p className="mb-1.5 text-xs text-ink/55">{periodLabel(last)}</p>}
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.gruppo ?? ''} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 text-sm" title={`${r.etichetta}: ${formatValue(r.valore, unit)}`}>
            <span className="truncate text-xs text-ink/70">{r.etichetta ?? r.gruppo ?? '—'}</span>
            <span className="h-3 rounded-r bg-black/[0.04]">
              <span className="block h-3 rounded-r-[4px]" style={{ width: `${max ? (r.valore! / max) * 100 : 0}%`, background: 'var(--color-accent)' }} />
            </span>
            <span className="text-xs tabular-nums text-ink/80">{formatValue(r.valore, unit)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** La tabella dei valori: la vista accessibile di ogni grafico. */
function ValuesTable({ points, unit }: { points: KpiPoint[]; unit: Unit }) {
  const grouped = points.some((p) => p.gruppo !== undefined);
  const prev = points.some((p) => p.anno_prima !== undefined);
  return (
    <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-black/[0.06]">
      <table className="w-full text-xs">
        <thead className="bg-paper text-left text-ink/55">
          <tr>
            <th className="px-2 py-1 font-medium">Periodo</th>
            {grouped && <th className="px-2 py-1 font-medium">Gruppo</th>}
            <th className="px-2 py-1 text-right font-medium">Valore</th>
            {prev && <th className="px-2 py-1 text-right font-medium">Anno prima</th>}
          </tr>
        </thead>
        <tbody>
          {[...points].reverse().map((p) => (
            <tr key={`${p.periodo}/${p.gruppo ?? ''}`} className="border-t border-black/[0.04]">
              <td className="px-2 py-1">{periodLabel(p.periodo)}</td>
              {grouped && <td className="px-2 py-1">{p.etichetta ?? '—'}</td>}
              <td className="px-2 py-1 text-right tabular-nums">{formatValue(p.valore, unit)}</td>
              {prev && <td className="px-2 py-1 text-right tabular-nums text-ink/55">{formatValue(p.anno_prima, unit)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Il numero principale: l'ultimo valore, con lo scarto sull'anno prima quando c'è. */
export function Headline({ k }: { k: KpiDto }) {
  const unit = unitOf(k);
  if (k.serie.some((p) => p.gruppo !== undefined)) return null;
  const last = [...k.serie].reverse().find((p) => p.valore != null);
  if (!last) return null;
  const prev = last.anno_prima;
  const delta = prev ? (last.valore! - prev) / Math.abs(prev) : null;
  return (
    <p className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-2xl font-semibold tabular-nums">{formatValue(last.valore, unit)}</span>
      {last.periodo !== 'totale' && <span className="text-xs text-ink/55">{periodLabel(last.periodo)}</span>}
      {delta !== null && (
        <span className="text-xs tabular-nums text-ink/70">
          {/* Fra due percentuali lo scarto è in punti, non una percentuale di una percentuale. */}
          {delta >= 0 ? '▲' : '▼'}{' '}
          {unit === 'percentuale'
            ? `${Math.abs((last.valore! - prev!) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })} punti`
            : `${Math.abs(delta * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`}{' '}
          sull’anno prima
        </span>
      )}
    </p>
  );
}

/** Il grafico di un indicatore, con la tabella dei valori a richiesta. */
export function KpiChart({ k }: { k: KpiDto }) {
  const [table, setTable] = useState(false);
  const unit = unitOf(k);
  const kind = k.libreria?.grafico ?? k.config.grafico ?? (k.config.grana ? 'colonne' : 'numero');
  const grouped = k.serie.some((p) => p.gruppo !== undefined);
  const timed = new Set(k.serie.map((p) => p.periodo)).size > 1 || (k.config.grana && !grouped);
  if (!k.serie.length) return null;
  return (
    <div>
      {kind === 'barre' || (grouped && !timed) ? (
        <GroupBars points={k.serie} unit={unit} />
      ) : kind !== 'numero' && timed ? (
        <TimeChart points={k.serie} unit={unit} kind={kind === 'linea' || grouped ? 'linea' : 'colonne'} />
      ) : null}
      <button type="button" className="mt-1 text-xs text-ink/50 hover:text-ink" onClick={() => setTable((v) => !v)}>
        {table ? 'Nascondi i valori' : 'Mostra i valori'}
      </button>
      {table && <ValuesTable points={k.serie} unit={unit} />}
    </div>
  );
}
